import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { BuyButton } from "./BuyButton";
import { scrollToSection } from "./constants";

const LINKS = [
  { label: "The Journal", id: "about", testId: "header-nav-about" },
  { label: "The Ritual", id: "ritual", testId: "header-nav-experience" },
  { label: "Why Gratitude", id: "benefits", testId: "header-nav-benefits" },
];

export const Nav = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1], delay: 0.2 }}
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
        scrolled
          ? "border-b border-linenborder/70 bg-linen/85 backdrop-blur-xl"
          : "border-b border-transparent bg-transparent"
      }`}
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4 sm:px-12 lg:px-20">
        <button
          data-testid="header-brand-logo"
          onClick={() => window.__lenis?.scrollTo(0, { duration: 1.4 })}
          className="text-left font-serif text-lg font-semibold leading-tight tracking-tight text-espresso sm:text-xl"
        >
          Love the Life
          <span className="block text-xs font-medium italic tracking-[0.18em] text-terracotta">
            you live
          </span>
        </button>

        <nav className="hidden items-center gap-9 md:flex">
          {LINKS.map((l) => (
            <button
              key={l.id}
              data-testid={l.testId}
              onClick={() => scrollToSection(l.id)}
              className="group relative text-sm font-medium text-walnut transition-colors duration-300 hover:text-espresso"
            >
              {l.label}
              <span className="absolute -bottom-1 left-0 h-px w-0 bg-terracotta transition-all duration-300 group-hover:w-full" />
            </button>
          ))}
        </nav>

        <div className="hidden md:block">
          <BuyButton
            testId="header-buy-now-button"
            label="Buy Now — $12.99"
            className="px-5 py-2.5 text-xs"
          />
        </div>

        <button
          data-testid="header-mobile-menu-button"
          onClick={() => setOpen(!open)}
          className="text-espresso md:hidden"
          aria-label="Toggle menu"
        >
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="overflow-hidden border-b border-linenborder bg-linen/95 backdrop-blur-xl md:hidden"
          >
            <div className="flex flex-col gap-5 px-6 py-6">
              {LINKS.map((l) => (
                <button
                  key={l.id}
                  data-testid={`${l.testId}-mobile`}
                  onClick={() => {
                    setOpen(false);
                    scrollToSection(l.id);
                  }}
                  className="text-left font-serif text-2xl text-espresso"
                >
                  {l.label}
                </button>
              ))}
              <BuyButton
                testId="header-buy-now-button-mobile"
                label="Buy Now — $12.99"
                className="w-fit"
              />
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
